<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetBillingListItem',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    item: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
    form: {
      type: Object,
      required: false,
    },
    isSaving: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const createInvoiceLineCalculator = (...args) => {
    const currency = _.find(currencies.value, { code: props.item.currency });
    if (currency == null) {
      return null;
    }
    return new $app.value.helpers.InvoiceLineCalculator(
      currency,
      taxCodes.value
    );
  };
  const rebuildInvoiceLineCalculator = (...args) => {
    setData('invoiceLineCalculator', createInvoiceLineCalculator());
  };
  const syncExchangeRate = (...args) => {
    const v = exchangeRate.value;
    props.item.exchange_rate = v.value == null ? null : v;
    if (invoiceLineCalculator.value) {
      invoiceLineCalculator.value.recalculateLineItem(props.item, 'base_price');
    }
  };
  const syncBasePrice = (...args) => {
    props.item.base_price = basePrice.value;
    if (invoiceLineCalculator.value) {
      invoiceLineCalculator.value.recalculateLineItem(props.item, 'base_price');
    }
  };
  const onContractChanged = (...args) => {
    const contract = props.item.contract;
    if (contract) {
      props.item.company = contract.company;
      props.item.partner = contract.client;
      props.item.rental = contract.rental;
    }
  };
  const onContractServiceChanged = (...args) => {
    const contract_service = props.item.contract_service;
    if (contract_service) {
      props.item.contract = contract_service.contract;
      props.item.description = contract_service.name;
      if (contract_service.invoice_notes) {
        props.item.notes = contract_service.invoice_notes;
      }
      onContractChanged();
      applyCurrentServicePrice(contract_service.id);
    }
  };
  const applyCurrentServicePrice = (...args) => {
    const [id] = args;
    $fn.value
      .fetch('read_contract_service', { parameters: { id } })
      .then((res) => {
        if (!res || !res.current_fee) {
          return;
        }

        const fee = res.current_fee;
        props.item.base_price = { amount: fee.amount, currency: fee.currency };
        setData('basePrice', props.item.base_price);

        if (props.item.currency != res.billing_currency) {
          props.item.currency = res.billing_currency;
          setData('invoiceLineCalculator', createInvoiceLineCalculator());
        }

        if (invoiceLineCalculator.value) {
          invoiceLineCalculator.value.applyContractServiceFee(
            props.item,
            res,
            res.current_fee
          );
        }
      });
  };
  const recalculate = (...args) => {
    const [field] = args;
    if (invoiceLineCalculator.value) {
      invoiceLineCalculator.value.recalculateLineItem(props.item, field);
    }
  };

  const currencies = ref(null);
  const taxCodes = ref(null);
  const invoiceLineCalculator = ref(null);
  const exchangeRate = ref(null);
  const basePrice = ref(null);
  const exchangeRateSources = ref(null);
  const ux = ref([]);
  const b_6610553441758910_parameters = computed(() => ({
    role: 'own',
  }));

  const $data = computed(() => ({
    currencies: currencies.value,
    taxCodes: taxCodes.value,
    invoiceLineCalculator: invoiceLineCalculator.value,
    exchangeRate: exchangeRate.value,
    basePrice: basePrice.value,
    exchangeRateSources: exchangeRateSources.value,
    ux: ux.value,
    b_6610553441758910_parameters: b_6610553441758910_parameters.value,
  }));

  const $$load_currencies = async () => {
    return $app.value.repo.get('list_currency');
  };
  const $$load_taxCodes = async () => {
    return $app.value.repo.get('list_tax_code');
  };
  const $$load_invoiceLineCalculator = async () => {
    return createInvoiceLineCalculator();
  };
  const $$load_exchangeRate = async () => {
    return (
      props.item.exchange_rate || { value: null, date: null, source: null }
    );
  };
  const $$load_basePrice = async () => {
    return _.extend({ amount: null, currency: null }, props.item.base_price);
  };
  const $$load_exchangeRateSources = async () => {
    return $app.value.repo.get('ui_metadata', {
      path: 'exchange_rate_source_options',
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'currencies' },
      { name: 'taxCodes' },
      { name: 'invoiceLineCalculator' },
      { name: 'exchangeRate' },
      { name: 'basePrice' },
      { name: 'exchangeRateSources' },
      { name: 'ux' },
      { name: 'b_6610553441758910_parameters', nodeId: '6610553441758910' },
    ],
    loaders: {
      currencies: $$load_currencies,
      taxCodes: $$load_taxCodes,
      invoiceLineCalculator: $$load_invoiceLineCalculator,
      exchangeRate: $$load_exchangeRate,
      basePrice: $$load_basePrice,
      exchangeRateSources: $$load_exchangeRateSources,
    },
    ux: {},
    dataRefs: {
      currencies,
      taxCodes,
      invoiceLineCalculator,
      exchangeRate,
      basePrice,
      exchangeRateSources,
      ux,
      b_6610553441758910_parameters,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isReadonly = computed(() => {
    return props.item.isReadonly || false;
  });
  const currentCurrency = computed(() => {
    if (loaded.value) {
      return _.find(currencies.value, { code: props.item.currency });
    }
    return null;
  });
  const digits = computed(() => {
    if (loaded.value) {
      return invoiceLineCalculator.value
        ? invoiceLineCalculator.value.digits
        : { quantity: 2, unitPrice: 2, amounts: 2 };
    }
    return null;
  });

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );
  watch(
    () => props.form,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('form');
    }
  );
  watch(
    () => props.isSaving,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('isSaving');
    }
  );

  const act_6466705138206980_script_9jobkd2na_value_callable = async (
    $event
  ) => {
    return onContractServiceChanged();
  };
  const act_6466705138206980_script_9jobkd2na_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6466705138206980_script_9jobkd2na_value_callable($event);
      },
    };
  };
  const act_3943151158818434_script_ur45ohtx5_value_callable = async (
    $event
  ) => {
    return onContractChanged();
  };
  const act_3943151158818434_script_ur45ohtx5_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3943151158818434_script_ur45ohtx5_value_callable($event);
      },
    };
  };
  const act_4795561206098500_script_zvv6girmz_value_callable = async (
    $event
  ) => {
    return syncBasePrice();
  };
  const act_4795561206098500_script_zvv6girmz_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4795561206098500_script_zvv6girmz_value_callable($event);
      },
    };
  };
  const act_6748526895113679_script_8t0ttpglq_value_callable = async (
    $event
  ) => {
    return syncBasePrice();
  };
  const act_6748526895113679_script_8t0ttpglq_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6748526895113679_script_8t0ttpglq_value_callable($event);
      },
    };
  };
  const act_1175399472650195_script_84mjx6pnq_value_callable = async (
    $event
  ) => {
    return syncExchangeRate();
  };
  const act_1175399472650195_script_84mjx6pnq_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1175399472650195_script_84mjx6pnq_value_callable($event);
      },
    };
  };
  const act_2786108381738342_script_efh0jauqx_value_callable = async (
    $event
  ) => {
    return syncExchangeRate();
  };
  const act_2786108381738342_script_efh0jauqx_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2786108381738342_script_efh0jauqx_value_callable($event);
      },
    };
  };
  const act_7888454496860501_script_7jzr4jx6n_value_callable = async (
    $event
  ) => {
    return syncExchangeRate();
  };
  const act_7888454496860501_script_7jzr4jx6n_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7888454496860501_script_7jzr4jx6n_value_callable($event);
      },
    };
  };
  const act_5418559842130999_script_kkkbr25c5_value_callable = async (
    $event
  ) => {
    return recalculate('quantity');
  };
  const act_5418559842130999_script_kkkbr25c5_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5418559842130999_script_kkkbr25c5_value_callable($event);
      },
    };
  };
  const act_7916821937533317_script_q8f1cf5zt_value_callable = async (
    $event
  ) => {
    return recalculate('price');
  };
  const act_7916821937533317_script_q8f1cf5zt_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7916821937533317_script_q8f1cf5zt_value_callable($event);
      },
    };
  };
  const act_4441335657753065_script_iosy5acni_value_callable = async (
    $event
  ) => {
    return rebuildInvoiceLineCalculator();
  };
  const act_4441335657753065_script_iosy5acni_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_4441335657753065_script_iosy5acni_value_callable($event);
      },
    };
  };
  const act_6529453708504681_script_hfuttbpp2_value_callable = async (
    $event
  ) => {
    return recalculate('discount');
  };
  const act_6529453708504681_script_hfuttbpp2_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6529453708504681_script_hfuttbpp2_value_callable($event);
      },
    };
  };
  const act_2837120413286864_script_0beuj9cuz_value_callable = async (
    $event
  ) => {
    return recalculate('net_amount');
  };
  const act_2837120413286864_script_0beuj9cuz_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2837120413286864_script_0beuj9cuz_value_callable($event);
      },
    };
  };
  const act_1200777560175496_script_qwt1u9mm0_value_callable = async (
    $event
  ) => {
    return recalculate('tax_code');
  };
  const act_1200777560175496_script_qwt1u9mm0_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1200777560175496_script_qwt1u9mm0_value_callable($event);
      },
    };
  };
  const act_6300315092833989_script_82d5oyzpl_value_callable = async (
    $event
  ) => {
    return recalculate('amount');
  };
  const act_6300315092833989_script_82d5oyzpl_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6300315092833989_script_82d5oyzpl_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_hyhrzvonu =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_9j8z9esiu =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_hyhrzvonu,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_9j8z9esiu],
    event: 'reload',
  });
  const act_6466705138206980_script_9jobkd2na = new actionTypes.ScriptAction({
    actionArgs: act_6466705138206980_script_9jobkd2na_getActionArgs,
  });
  const inst_evh_6466705138206980_optionSelected_8acmwvlsn =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_script_9jobkd2na,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_6466705138206980_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_6466705138206980_optionSelected_8acmwvlsn],
      event: 'optionSelected',
    });
  const act_3943151158818434_script_ur45ohtx5 = new actionTypes.ScriptAction({
    actionArgs: act_3943151158818434_script_ur45ohtx5_getActionArgs,
  });
  const inst_evh_3943151158818434_optionSelected_hnaomuqb7 =
    new actionTypes.EventHandlerImpl({
      action: act_3943151158818434_script_ur45ohtx5,
      event: 'optionSelected',
      displayName: 'script',
    });
  const inst_evh_3943151158818434_optionSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3943151158818434_optionSelected_hnaomuqb7],
      event: 'optionSelected',
    });
  const act_4795561206098500_script_zvv6girmz = new actionTypes.ScriptAction({
    actionArgs: act_4795561206098500_script_zvv6girmz_getActionArgs,
  });
  const inst_evh_4795561206098500_blur_nkjur2ou7 =
    new actionTypes.EventHandlerImpl({
      action: act_4795561206098500_script_zvv6girmz,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_4795561206098500_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4795561206098500_blur_nkjur2ou7],
    event: 'blur',
  });
  const act_6748526895113679_script_8t0ttpglq = new actionTypes.ScriptAction({
    actionArgs: act_6748526895113679_script_8t0ttpglq_getActionArgs,
  });
  const inst_evh_6748526895113679_input_vhuqw4k79 =
    new actionTypes.EventHandlerImpl({
      action: act_6748526895113679_script_8t0ttpglq,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_6748526895113679_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6748526895113679_input_vhuqw4k79],
    event: 'input',
  });
  const act_1175399472650195_script_84mjx6pnq = new actionTypes.ScriptAction({
    actionArgs: act_1175399472650195_script_84mjx6pnq_getActionArgs,
  });
  const inst_evh_1175399472650195_blur_xnvafn53s =
    new actionTypes.EventHandlerImpl({
      action: act_1175399472650195_script_84mjx6pnq,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_1175399472650195_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1175399472650195_blur_xnvafn53s],
    event: 'blur',
  });
  const act_2786108381738342_script_efh0jauqx = new actionTypes.ScriptAction({
    actionArgs: act_2786108381738342_script_efh0jauqx_getActionArgs,
  });
  const inst_evh_2786108381738342_blur_c2ppqj2fm =
    new actionTypes.EventHandlerImpl({
      action: act_2786108381738342_script_efh0jauqx,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_2786108381738342_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2786108381738342_blur_c2ppqj2fm],
    event: 'blur',
  });
  const act_7888454496860501_script_7jzr4jx6n = new actionTypes.ScriptAction({
    actionArgs: act_7888454496860501_script_7jzr4jx6n_getActionArgs,
  });
  const inst_evh_7888454496860501_blur_wp0lnefd6 =
    new actionTypes.EventHandlerImpl({
      action: act_7888454496860501_script_7jzr4jx6n,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_7888454496860501_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7888454496860501_blur_wp0lnefd6],
    event: 'blur',
  });
  const act_5418559842130999_script_kkkbr25c5 = new actionTypes.ScriptAction({
    actionArgs: act_5418559842130999_script_kkkbr25c5_getActionArgs,
  });
  const inst_evh_5418559842130999_blur_luncpzn0c =
    new actionTypes.EventHandlerImpl({
      action: act_5418559842130999_script_kkkbr25c5,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_5418559842130999_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5418559842130999_blur_luncpzn0c],
    event: 'blur',
  });
  const act_7916821937533317_script_q8f1cf5zt = new actionTypes.ScriptAction({
    actionArgs: act_7916821937533317_script_q8f1cf5zt_getActionArgs,
  });
  const inst_evh_7916821937533317_blur_gz1v2w9oo =
    new actionTypes.EventHandlerImpl({
      action: act_7916821937533317_script_q8f1cf5zt,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_7916821937533317_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7916821937533317_blur_gz1v2w9oo],
    event: 'blur',
  });
  const act_4441335657753065_script_iosy5acni = new actionTypes.ScriptAction({
    actionArgs: act_4441335657753065_script_iosy5acni_getActionArgs,
  });
  const inst_evh_4441335657753065_input_av75xsp9u =
    new actionTypes.EventHandlerImpl({
      action: act_4441335657753065_script_iosy5acni,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_4441335657753065_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4441335657753065_input_av75xsp9u],
    event: 'input',
  });
  const act_6529453708504681_script_hfuttbpp2 = new actionTypes.ScriptAction({
    actionArgs: act_6529453708504681_script_hfuttbpp2_getActionArgs,
  });
  const inst_evh_6529453708504681_blur_p7zuqmfgg =
    new actionTypes.EventHandlerImpl({
      action: act_6529453708504681_script_hfuttbpp2,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_6529453708504681_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6529453708504681_blur_p7zuqmfgg],
    event: 'blur',
  });
  const act_2837120413286864_script_0beuj9cuz = new actionTypes.ScriptAction({
    actionArgs: act_2837120413286864_script_0beuj9cuz_getActionArgs,
  });
  const inst_evh_2837120413286864_blur_8oc3qnozi =
    new actionTypes.EventHandlerImpl({
      action: act_2837120413286864_script_0beuj9cuz,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_2837120413286864_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2837120413286864_blur_8oc3qnozi],
    event: 'blur',
  });
  const act_1200777560175496_script_qwt1u9mm0 = new actionTypes.ScriptAction({
    actionArgs: act_1200777560175496_script_qwt1u9mm0_getActionArgs,
  });
  const inst_evh_1200777560175496_input_7rr3bugvn =
    new actionTypes.EventHandlerImpl({
      action: act_1200777560175496_script_qwt1u9mm0,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_1200777560175496_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1200777560175496_input_7rr3bugvn],
    event: 'input',
  });
  const act_6300315092833989_script_82d5oyzpl = new actionTypes.ScriptAction({
    actionArgs: act_6300315092833989_script_82d5oyzpl_getActionArgs,
  });
  const inst_evh_6300315092833989_blur_sr3ihgk5k =
    new actionTypes.EventHandlerImpl({
      action: act_6300315092833989_script_82d5oyzpl,
      event: 'blur',
      displayName: 'script',
    });
  const inst_evh_6300315092833989_blur = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6300315092833989_blur_sr3ihgk5k],
    event: 'blur',
  });

  const actions = {
    inst_evh_7315092382398562_reload_9j8z9esiu,
    inst_evh_7315092382398562_reload,
    inst_evh_6466705138206980_optionSelected_8acmwvlsn,
    inst_evh_6466705138206980_optionSelected,
    inst_evh_3943151158818434_optionSelected_hnaomuqb7,
    inst_evh_3943151158818434_optionSelected,
    inst_evh_4795561206098500_blur_nkjur2ou7,
    inst_evh_4795561206098500_blur,
    inst_evh_6748526895113679_input_vhuqw4k79,
    inst_evh_6748526895113679_input,
    inst_evh_1175399472650195_blur_xnvafn53s,
    inst_evh_1175399472650195_blur,
    inst_evh_2786108381738342_blur_c2ppqj2fm,
    inst_evh_2786108381738342_blur,
    inst_evh_7888454496860501_blur_wp0lnefd6,
    inst_evh_7888454496860501_blur,
    inst_evh_5418559842130999_blur_luncpzn0c,
    inst_evh_5418559842130999_blur,
    inst_evh_7916821937533317_blur_gz1v2w9oo,
    inst_evh_7916821937533317_blur,
    inst_evh_4441335657753065_input_av75xsp9u,
    inst_evh_4441335657753065_input,
    inst_evh_6529453708504681_blur_p7zuqmfgg,
    inst_evh_6529453708504681_blur,
    inst_evh_2837120413286864_blur_8oc3qnozi,
    inst_evh_2837120413286864_blur,
    inst_evh_1200777560175496_input_7rr3bugvn,
    inst_evh_1200777560175496_input,
    inst_evh_6300315092833989_blur_sr3ihgk5k,
    inst_evh_6300315092833989_blur,
  };

  const evh_7315092382398562_reload_9j8z9esiu = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_9j8z9esiu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_optionSelected_8acmwvlsn = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_6466705138206980_optionSelected_8acmwvlsn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_optionSelected = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_optionSelected_hnaomuqb7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3943151158818434_optionSelected_hnaomuqb7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_optionSelected = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_optionSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4795561206098500_blur_nkjur2ou7 = async (event, scope) => {
    return await actions.inst_evh_4795561206098500_blur_nkjur2ou7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4795561206098500_blur = async (event, scope) => {
    return await actions.inst_evh_4795561206098500_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6748526895113679_input_vhuqw4k79 = async (event, scope) => {
    return await actions.inst_evh_6748526895113679_input_vhuqw4k79.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6748526895113679_input = async (event, scope) => {
    return await actions.inst_evh_6748526895113679_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1175399472650195_blur_xnvafn53s = async (event, scope) => {
    return await actions.inst_evh_1175399472650195_blur_xnvafn53s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1175399472650195_blur = async (event, scope) => {
    return await actions.inst_evh_1175399472650195_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2786108381738342_blur_c2ppqj2fm = async (event, scope) => {
    return await actions.inst_evh_2786108381738342_blur_c2ppqj2fm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2786108381738342_blur = async (event, scope) => {
    return await actions.inst_evh_2786108381738342_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7888454496860501_blur_wp0lnefd6 = async (event, scope) => {
    return await actions.inst_evh_7888454496860501_blur_wp0lnefd6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7888454496860501_blur = async (event, scope) => {
    return await actions.inst_evh_7888454496860501_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5418559842130999_blur_luncpzn0c = async (event, scope) => {
    return await actions.inst_evh_5418559842130999_blur_luncpzn0c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5418559842130999_blur = async (event, scope) => {
    return await actions.inst_evh_5418559842130999_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7916821937533317_blur_gz1v2w9oo = async (event, scope) => {
    return await actions.inst_evh_7916821937533317_blur_gz1v2w9oo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7916821937533317_blur = async (event, scope) => {
    return await actions.inst_evh_7916821937533317_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4441335657753065_input_av75xsp9u = async (event, scope) => {
    return await actions.inst_evh_4441335657753065_input_av75xsp9u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4441335657753065_input = async (event, scope) => {
    return await actions.inst_evh_4441335657753065_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6529453708504681_blur_p7zuqmfgg = async (event, scope) => {
    return await actions.inst_evh_6529453708504681_blur_p7zuqmfgg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6529453708504681_blur = async (event, scope) => {
    return await actions.inst_evh_6529453708504681_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2837120413286864_blur_8oc3qnozi = async (event, scope) => {
    return await actions.inst_evh_2837120413286864_blur_8oc3qnozi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2837120413286864_blur = async (event, scope) => {
    return await actions.inst_evh_2837120413286864_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1200777560175496_input_7rr3bugvn = async (event, scope) => {
    return await actions.inst_evh_1200777560175496_input_7rr3bugvn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1200777560175496_input = async (event, scope) => {
    return await actions.inst_evh_1200777560175496_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6300315092833989_blur_sr3ihgk5k = async (event, scope) => {
    return await actions.inst_evh_6300315092833989_blur_sr3ihgk5k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6300315092833989_blur = async (event, scope) => {
    return await actions.inst_evh_6300315092833989_blur.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div v-if="loaded">
      <ItpFormGroup
        name="contract_service"
        :label="pgettext('BillingListItem', 'Contract service')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
      >
        <UcContractServiceSelector
          :item="item"
          path="contract_service"
          :readonly="isReadonly"
          clearable
          @optionSelected="evh_6466705138206980_optionSelected($event, {})"
        >
        </UcContractServiceSelector>
      </ItpFormGroup>
      <ItpFormGroup
        name="contract"
        :label="pgettext('BillingListItem', 'Contract')"
        :label-cols-md="3"
        :content-cols-md="8"
        :labelAlign="labelAlign"
      >
        <UcContractSelector
          name="contract"
          :item="item"
          path="contract"
          :readonly="isReadonly"
          clearable
          @optionSelected="evh_3943151158818434_optionSelected($event, {})"
        >
        </UcContractSelector>
      </ItpFormGroup>
      <ItpFormGroup
        name="description"
        :label="pgettext('BillingListItem', 'Description')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        required
      >
        <ItpFormInput
          name="description"
          v-model="item.description"
          :value="item.description"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.description =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="isReadonly"
          required
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        name="notes"
        :label="pgettext('BillingListItem', 'Note')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
      >
        <ItpFormTextarea
          name="notes"
          v-model="item.notes"
          :value="item.notes"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.notes =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="isReadonly"
        >
        </ItpFormTextarea>
      </ItpFormGroup>
      <ItpFormGroup
        name="billing_category"
        :label="pgettext('BillingListItem', 'Billing category')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
      >
        <ItpFormInput
          name="billing_category"
          v-model="item.billing_category"
          :value="item.billing_category"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.billing_category =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :readonly="isReadonly"
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="3"
        name="invoice_date"
        :label="pgettext('BillingListItem', 'Billing due date')"
        :labelAlign="labelAlign"
        required
      >
        <ItpFormDatetime
          name="invoice_date"
          v-model="item.invoice_date"
          :readonly="isReadonly"
          required
        >
        </ItpFormDatetime>
      </ItpFormGroup>
      <ItpFormGroup
        name="company"
        :label="pgettext('BillingListItem', 'Billing company')"
        :label-cols-md="3"
        :content-cols-md="8"
        :labelAlign="labelAlign"
        required
      >
        <UcCompanySelector
          name="company"
          :item="item"
          path="company"
          :parameters="b_6610553441758910_parameters"
          required
          :readonly="isReadonly"
        >
        </UcCompanySelector>
      </ItpFormGroup>
      <ItpFormGroup
        name="client"
        :label="pgettext('BillingListItem', 'Client')"
        :label-cols-md="3"
        :content-cols-md="8"
        :labelAlign="labelAlign"
        required
      >
        <UcCompanySelector
          name="partner"
          :item="item"
          path="partner"
          required
          :readonly="isReadonly"
        >
        </UcCompanySelector>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="3"
        name="due_date"
        :label="pgettext('BillingListItem', 'Payment due date')"
        :labelAlign="labelAlign"
        required
      >
        <ItpFormDatetime
          name="due_date"
          v-model="item.due_date"
          required
          :readonly="isReadonly"
        >
        </ItpFormDatetime>
      </ItpFormGroup>
      <ItpFormGroup
        name="period"
        :label="pgettext('BillingListItem', 'Billing period')"
        :label-cols-md="3"
        :content-cols-md="9"
        :labelAlign="labelAlign"
        contentClass="row"
        v-if="item.period"
      >
        <div class="col-4">
          <ItpFormDatetime
            name="billing_period_start"
            v-model="item.period.start"
            :readonly="isReadonly"
            :placeholder="pgettext('BillingPeriod', 'From')"
          >
          </ItpFormDatetime>
        </div>
        <div class="col-4">
          <ItpFormDatetime
            name="billing_period_end"
            v-model="item.period.end"
            :placeholder="pgettext('BillingPeriod', 'To')"
            :readonly="isReadonly"
          >
          </ItpFormDatetime>
        </div>
      </ItpFormGroup>
      <hr />
      <ItpFormGroup
        name="base_price"
        :label="pgettext('BillingListItem', 'Base price')"
        :label-cols-md="3"
        :content-cols-md="8"
        :labelAlign="labelAlign"
        contentClass="row"
      >
        <div class="col-4">
          <ItpFormInput
            name="base_price_amount"
            mask="number"
            unmask="typed"
            :scale="digits.amounts"
            v-model="basePrice.amount"
            :value="basePrice.amount"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (basePrice.amount =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadonly"
            @blur="evh_4795561206098500_blur($event, {})"
          >
          </ItpFormInput>
        </div>
        <div class="col-3">
          <ItpFormSelect2
            name="base_price_currency"
            v-model="basePrice.currency"
            :value="basePrice.currency"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
                (basePrice.currency =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event);
              evh_6748526895113679_input($event, {});
            "
            :options="currencies"
            valueField="code"
            textField="code"
            :readonly="isReadonly"
          >
          </ItpFormSelect2>
        </div>
      </ItpFormGroup>
      <ItpFormGroup
        name="currency"
        :label="pgettext('BillingListItem', 'Exchange rate')"
        :label-cols-md="3"
        :content-cols-md="8"
        :labelAlign="labelAlign"
        contentClass="row"
      >
        <div class="col-3">
          <ItpFormInput
            mask="number"
            unmask="typed"
            :scale="2"
            name="exchange_rate"
            v-model="exchangeRate.value"
            :value="exchangeRate.value"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (exchangeRate.value =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :readonly="isReadonly"
            :placeholder="pgettext('Invoice', 'Rate')"
            @blur="evh_1175399472650195_blur($event, {})"
          >
          </ItpFormInput>
        </div>
        <div class="col-4">
          <ItpFormDatetime
            name="exchange_rate_date"
            v-model="exchangeRate.date"
            :readonly="isReadonly"
            :placeholder="pgettext('Invoice', 'Date')"
            @blur="evh_2786108381738342_blur($event, {})"
          >
          </ItpFormDatetime>
        </div>
        <div class="col-5">
          <ItpFormSelect
            name="exchange_rate_source"
            v-model="exchangeRate.source"
            :value="exchangeRate.source"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (exchangeRate.source =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            :options="exchangeRateSources"
            :readonly="isReadonly"
            :placeholder="pgettext('ExchangeRateFieldset', 'Source')"
            valueField="value"
            textField="name"
            null-option
            @blur="evh_7888454496860501_blur($event, {})"
          >
          </ItpFormSelect>
        </div>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="8"
        name="quantity"
        :label="pgettext('BillingListItem', 'Quantity')"
        :labelAlign="labelAlign"
        required
        contentClass="row"
      >
        <div class="col-4">
          <ItpFormInput
            mask="number"
            unmask="typed"
            :scale="digits.quantity"
            name="quantity"
            v-model="item.quantity"
            :value="item.quantity"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.quantity =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            :readonly="isReadonly"
            @blur="evh_5418559842130999_blur($event, {})"
          >
          </ItpFormInput>
        </div>
        <div class="col-3">
          <ItpFormInput
            name="unit"
            v-model="item.unit"
            :value="item.unit"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.unit =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            :readonly="isReadonly"
          >
          </ItpFormInput>
        </div>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="8"
        name="price"
        :label="pgettext('BillingListItem', 'Price')"
        :labelAlign="labelAlign"
        required
        contentClass="row"
      >
        <div class="col-4">
          <ItpFormInput
            name="price"
            mask="number"
            unmask="typed"
            :scale="digits.unitPrice"
            v-model="item.price"
            :value="item.price"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
              (item.price =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event)
            "
            required
            :readonly="isReadonly"
            @blur="evh_7916821937533317_blur($event, {})"
          >
          </ItpFormInput>
        </div>
        <div class="col-3">
          <ItpFormSelect2
            name="currency"
            v-model="item.currency"
            :value="item.currency"
            @input="
              !($event && typeof $event.stopPropagation === 'function') &&
                (item.currency =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event);
              evh_4441335657753065_input($event, {});
            "
            :options="currencies"
            valueField="code"
            textField="code"
            :readonly="isReadonly"
          >
          </ItpFormSelect2>
        </div>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="2"
        name="discount"
        :label="pgettext('BillingListItem', 'Discount')"
        :labelAlign="labelAlign"
        required
        contentClass="input-group"
      >
        <ItpFormInput
          name="discount"
          type="number"
          v-model="item.discount"
          :value="item.discount"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.discount =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :min="0"
          :max="100"
          required
          :readonly="isReadonly"
          @blur="evh_6529453708504681_blur($event, {})"
        >
        </ItpFormInput>
        <div class="input-group-append">
          <span class="input-group-text"> &percnt; </span>
        </div>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="2"
        name="net_amount"
        :label="pgettext('BillingListItem', 'Net')"
        :labelAlign="labelAlign"
        required
      >
        <ItpFormInput
          name="net_amount"
          mask="number"
          unmask="typed"
          :scale="digits.amounts"
          v-model="item.net_amount"
          :value="item.net_amount"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.net_amount =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          required
          :readonly="isReadonly"
          @blur="evh_2837120413286864_blur($event, {})"
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="2"
        name="tax_code"
        :label="pgettext('BillingListItem', 'VAT code')"
        :labelAlign="labelAlign"
        required
      >
        <ItpFormSelect2
          name="tax_code"
          v-model="item.tax_code"
          :value="item.tax_code"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
              (item.tax_code =
                $event && typeof $event === 'object' && 'data' in $event
                  ? $event.data
                  : $event);
            evh_1200777560175496_input($event, {});
          "
          :options="taxCodes"
          valueField="id"
          textField="id"
          required
          :readonly="isReadonly"
        >
        </ItpFormSelect2>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="3"
        :content-cols-md="2"
        name="amount"
        :label="pgettext('BillingListItem', 'Amount')"
        :labelAlign="labelAlign"
        required
      >
        <ItpFormInput
          name="amount"
          mask="number"
          unmask="typed"
          :scale="digits.amounts"
          v-model="item.amount"
          :value="item.amount"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.amount =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          required
          :readonly="isReadonly"
          @blur="evh_6300315092833989_blur($event, {})"
        >
        </ItpFormInput>
      </ItpFormGroup>
    </div>
  </ItpUsercontrol>
</template>
